<template>
  <!--
    Skills editor — gallery-driven one-screen view.

    Iteration 2 (2026-05-05): merged DeckInfoModal + DeckPreviewModal
    into a single DeckPickerModal so clicking ANYWHERE on a tile opens
    one combined view (large back + brand description + 78-card grid +
    Select Deck button). The previous per-tile i/eye overlay icons
    are gone — the whole tile is the click target.

    Active deck prominence is intentionally heavy: a full-width gold
    banner across the top of the tile + 3px gold border + outer glow
    + brighter name below. Three layered cues so it can't be missed.

    Tarot only for now. Astrology variant still uses the old hero
    layout — to be revisited.

    Settings modal unchanged per direction ("leave it as is for the
    moment").
  -->
  <div class="skills-editor">
    <!-- Skill tab strip: one tab per active variant + "+ Add new Skill"
         stub. First variant is selected by default; operator clicks
         to switch which skill they're editing. Each tab swaps the
         entire panel below.

         Consumers can pass :hide-skill-tabs="true" to suppress this
         strip when the parent already shows skill identity above —
         e.g. ReadingSessionPanel's redesigned Session Settings header.
         The active variant still defaults to the first one so the
         Deck pane keeps working. -->
    <div v-if="variants.length && !hideSkillTabs" class="skill-tabs" role="tablist">
      <button
        v-for="v in variants"
        :key="v.id"
        role="tab"
        type="button"
        class="skill-tab"
        :class="{ active: v.id === activeVariantId }"
        :aria-selected="v.id === activeVariantId"
        @click="activeVariantId = v.id"
      >
        <span class="tab-icon">{{ v.skill_key === 'tarot' ? '🎴' : '✨' }}</span>
        <span class="tab-name">{{ formatSkill(v.skill_key) }}</span>
      </button>
      <button
        role="tab"
        type="button"
        class="skill-tab add-skill"
        :class="{ active: activeVariantId === '__add__' }"
        :aria-selected="activeVariantId === '__add__'"
        @click="activeVariantId = '__add__'"
      >+ Add new Skill</button>
    </div>

    <!-- Add new Skill stub — also gated on hideSkillTabs so the
         duplicated strip and its companion stub disappear together
         when the parent owns skill identity. -->
    <div
      v-if="!hideSkillTabs && activeVariantId === '__add__'"
      class="add-skill-stub"
    >
      <div class="stub-icon">✦</div>
      <h4>Add a new skill</h4>
      <p>
        Coming soon - pick another practice (astrology, dreamwork,
        I-Ching, etc.) for this reader. Today, each reader runs a
        single core skill.
      </p>
    </div>

    <div v-for="v in variants" :key="v.id" v-show="v.id === activeVariantId" class="variant-card">
      <!-- Inline "select box" for variant_key — popover overlay,
           no layout shift when expanded. Replaces the prior
           Settings modal + the "Tarot · Hawaii Surf" summary line.

           Suppressed when hideSkillTabs is set: the consumer (e.g.
           ReadingSessionPanel's redesigned Session Settings header)
           renders the variant picker itself at panel-level, so this
           inline copy would just duplicate it inside the Deck pane. -->
      <header v-if="!hideSkillTabs" class="ctx-bar">
        <SkillSettingsInline
          :variant="v"
          @saved="onSettingsSaved"
        />
      </header>

      <!-- Tarot: 3-tab editor — My Decks (default) | Browse | Build -->
      <template v-if="v.skill_key === 'tarot'">
        <div class="mode-tabs" role="tablist">
          <button
            role="tab"
            class="mode-tab"
            :class="{ active: mode === 'my-decks' }"
            :aria-selected="mode === 'my-decks'"
            @click="mode = 'my-decks'"
          >My Decks</button>
          <button
            role="tab"
            class="mode-tab"
            :class="{ active: mode === 'browse' }"
            :aria-selected="mode === 'browse'"
            @click="mode = 'browse'"
          >Browse</button>
          <button
            role="tab"
            class="mode-tab"
            :class="{ active: mode === 'build' }"
            :aria-selected="mode === 'build'"
            @click="mode = 'build'"
          >Build Custom Deck</button>
        </div>

        <!-- My Decks mode: Favorites + Custom Decks sections -->
        <div v-if="mode === 'my-decks'">
          <MyDecksView
            :favorites="favorites"
            :custom-decks="customDecks"
            :active-decks="activeDecks"
            :loading="loadingMyDecks"
            @go-browse="mode = 'browse'"
            @go-build="mode = 'build'; myDecksBuilderSlug = null"
            @open-favorite="onFavoriteClicked($event, v)"
            @open-custom="onCustomDeckClicked($event)"
            @delete-custom="onDeleteCustomDeck($event)"
          />
        </div>

        <!-- Browse mode: platform decks gallery -->
        <div v-else-if="mode === 'browse'" class="gallery-wrap">
          <div v-if="loadingDecks" class="status">Loading decks…</div>
          <div v-else-if="!decks.length" class="status">
            No decks available.
          </div>
          <div v-else class="gallery">
            <button
              v-for="d in orderDecks(decks, currentDeckSlug(v))"
              :key="d.slug"
              type="button"
              class="tile"
              :class="{ current: d.slug === currentDeckSlug(v) }"
              @click="openDeckPicker(d, v)"
              :aria-label="d.slug === currentDeckSlug(v)
                ? d.name + ' (active deck) - open to view cards'
                : d.name + ' - open to view and pick this deck'"
            >
              <div class="tile-back">
                <!-- Pinned inside the frame: the row never shifts for it. -->
                <span v-if="d.slug === currentDeckSlug(v)" class="active-ribbon">Active</span>
                <img
                  v-if="backUrlOf(d)"
                  :src="backUrlOf(d)"
                  :alt="d.name + ' card back'"
                />
                <div v-else class="tile-placeholder">
                  <span class="placeholder-icon">✦</span>
                  <span>No back generated yet</span>
                </div>
              </div>
              <div class="tile-name">{{ d.name }}</div>
            </button>
          </div>
        </div>

        <!-- Build Custom Deck mode: empty form -->
        <div v-else>
          <CustomDeckBuilder
            :slug="null"
            @done="onBuilderDone"
            @cancel="mode = 'my-decks'"
          />
        </div>

      </template>

      <!-- Astrology: keep prior hero+swap until next refactor -->
      <div v-else class="astrology-fallback">
        <div class="hero-frame">
          <img
            v-if="heroUrl(v)"
            :src="heroUrl(v)"
            :alt="heroAlt(v)"
            class="hero-img"
          />
          <div v-else class="hero-placeholder">No cover image</div>
        </div>
        <h4 class="hero-title">{{ heroTitle(v) }}</h4>
        <button class="primary-btn" @click="symbologySwapVariant = v">
          Swap symbology
        </button>
      </div>
    </div>

    <!-- ── Modals ───────────────────────────────────────────────── -->
    <!-- Single combined modal (info + 78-card grid + Select Deck).   -->
    <!-- Replaces the previous DeckInfoModal + DeckPreviewModal split. -->
    <DeckPickerModal
      v-if="pickerDeck"
      :deck="pickerDeck"
      :variant="pickerVariant"
      @close="closePicker"
      @selected="closePicker"
    />
    <SymbologySwapModal
      v-if="symbologySwapVariant"
      :variant="symbologySwapVariant"
      @close="symbologySwapVariant = null"
      @saved="symbologySwapVariant = null"
    />

    <!-- Custom-deck detail modal — large overlay with the per-deck
         builder. Triggered when the operator clicks a custom deck
         tile in My Decks. Replaces the previous inline view so the
         deck gets the full screen real estate. -->
    <div
      v-if="myDecksBuilderSlug"
      class="deck-detail-backdrop"
      @click.self="returnToMyDecks"
    >
      <div class="deck-detail-card">
        <button
          class="deck-detail-close"
          aria-label="Close"
          @click="returnToMyDecks"
        >×</button>
        <div class="deck-detail-body">
          <CustomDeckBuilder
            :slug="myDecksBuilderSlug"
            @done="returnToMyDecks"
            @cancel="returnToMyDecks"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { store } from '@/store'
import { orderDecks } from '@/utils/deck-gallery'
import * as api from '@/api-service.js'
import DeckPickerModal from './DeckPickerModal.vue'
import SymbologySwapModal from './SymbologySwapModal.vue'
import SkillSettingsInline from './SkillSettingsInline.vue'
import MyDecksView from './MyDecksView.vue'
import CustomDeckBuilder from './CustomDeckBuilder.vue'

// Lets a parent (e.g. ReadingSessionPanel) suppress this component's
// own skill-tab strip + Add-new-Skill stub when the parent already
// renders skill identity — and, since it then owns the choice, name
// the variant this pane should show.
const props = defineProps({
  hideSkillTabs: { type: Boolean, default: false },
  // The variant the parent has selected (ReaderSkillVariant id). With the
  // tabs hidden there is no other way to pick one, and "first in the list"
  // was the astrology fallback for any reader whose astrology variant
  // sorts ahead of tarot (prod 2026-09-20: Founder Lee's Card Deck read
  // "No cover image / Untitled" over a perfectly good deck).
  variantId: { type: [Number, String], default: null },
})

const variants = computed(() => (
  (store.readerSkillVariants || []).filter(v => v.is_active !== false)
))

// Skill tab state — which variant is currently visible. The parent's
// variantId wins when it names one we have; otherwise default to the
// first variant. "__add__" sentinel is the Add new Skill stub.
const activeVariantId = ref(null)
watch(
  [variants, () => props.variantId],
  ([list, wanted]) => {
    if (!list.length) { activeVariantId.value = null; return }
    if (wanted != null && list.some(v => v.id === wanted)) {
      activeVariantId.value = wanted
      return
    }
    if (
      activeVariantId.value === null ||
      activeVariantId.value === '__add__' ||
      !list.some(v => v.id === activeVariantId.value)
    ) {
      activeVariantId.value = list[0].id
    }
  },
  { immediate: true },
)

// ── Mode tabs (My Decks default | Browse | Build Custom Deck) ──
const mode = ref('my-decks')

// When operator clicks a custom deck tile in My Decks, render the
// per-deck builder INLINE inside the My Decks pane (with a back link)
// instead of pushing them to the Build tab. Null = show the gallery.
const myDecksBuilderSlug = ref(null)

// ── My Decks data (favorites + custom decks) ────────────────────
const favorites = ref([])
const customDecks = ref([])
const loadingMyDecks = ref(false)

async function loadMyDecks() {
  loadingMyDecks.value = true
  try {
    const [favs, decks] = await Promise.all([
      api.listCustomDeckFavorites(),
      api.listMyCustomDecks(),
    ])
    favorites.value = favs || []
    customDecks.value = decks || []
  } finally {
    loadingMyDecks.value = false
  }
}
onMounted(loadMyDecks)

// The active deck may be a platform-pool deck that was never favorited — it still
// belongs in My Decks. Pull it from the browse pool (same shape as favorites) when
// it's not already in favorites/custom decks, so the chosen deck always shows.
const activeDecks = computed(() => {
  const activeSlugs = new Set(
    (store.readerSkillVariants || [])
      .filter(v => v.is_active !== false && v.deck_template_slug)
      .map(v => v.deck_template_slug))
  if (!activeSlugs.size) return []
  const have = new Set([...favorites.value, ...customDecks.value].map(d => d.slug))
  return (decks.value || []).filter(d => activeSlugs.has(d.slug) && !have.has(d.slug))
})
// Refresh My Decks when the operator returns to that tab so newly
// added favorites or freshly built decks show up.
watch(mode, (next) => {
  if (next === 'my-decks') loadMyDecks()
})

function onFavoriteClicked(deck, variant) {
  // Open the picker modal so they can Set as Default or Remove.
  openDeckPicker(deck, variant)
}
function onCustomDeckClicked(deck) {
  // Open the per-deck builder inline. Stays inside the My Decks tab
  // with a back link to return to the gallery.
  myDecksBuilderSlug.value = deck.slug
}
function returnToMyDecks() {
  myDecksBuilderSlug.value = null
  loadMyDecks()
}
function onBuilderDone() {
  // Operator hit Publish in the build tab → bounce back to My Decks
  // and refresh so the new deck shows up.
  mode.value = 'my-decks'
  myDecksBuilderSlug.value = null
  loadMyDecks()
}

async function onDeleteCustomDeck(deck) {
  const ok = confirm(
    `Delete "${deck.name}"? This is permanent - the cards and the brand will be removed.`
  )
  if (!ok) return
  try {
    await api.deleteCustomDeck(deck.slug)
    await loadMyDecks()
  } catch (e) {
    alert(e?.response?.data?.error || 'Could not delete this deck.')
  }
}

// Notify ReaderPage so the studio chat can route Custom Deck typing
// through the BE Aida (CustomDeckBuilder state class) instead of
// the keyword-based STUDIO_NAV_MAP. ReaderPage listens for these
// window events and toggles ``customDeckChatActive``.
// "custom" mode is anywhere the operator might be working with their
// custom deck — the My Decks tab when they've drilled into a custom
// deck, OR the Build Custom Deck tab. Either way the chat routing
// should be active so typed messages go to the BE Aida.
watch([mode, myDecksBuilderSlug], ([nextMode, nextBuilderSlug], [prevMode, prevBuilderSlug]) => {
  const inCustomNow = (
    nextMode === 'build' ||
    (nextMode === 'my-decks' && !!nextBuilderSlug)
  )
  const wasInCustom = (
    prevMode === 'build' ||
    (prevMode === 'my-decks' && !!prevBuilderSlug)
  )
  if (inCustomNow && !wasInCustom) {
    window.dispatchEvent(new CustomEvent('custom-deck:mode-entered'))
  } else if (!inCustomNow && wasInCustom) {
    window.dispatchEvent(new CustomEvent('custom-deck:mode-exited'))
  }
})

// ── Deck gallery state ─────────────────────────────────────────
const decks = ref([])
const loadingDecks = ref(true)

async function loadDecks() {
  loadingDecks.value = true
  try {
    const data = await api.browseDeckTemplates()
    decks.value = data?.items || []
  } finally {
    loadingDecks.value = false
  }
}
onMounted(loadDecks)

// ── Modal state ────────────────────────────────────────────────
// Combined picker modal — opens with the clicked deck + the variant
// being edited. The modal handles its own card-list fetch + the
// chooseDeckTemplate POST when the operator hits Select Deck.
const pickerDeck = ref(null)
const pickerVariant = ref(null)
const symbologySwapVariant = ref(null)

// ── Helpers ────────────────────────────────────────────────────
function backUrlOf(d) {
  return d?.back_image_url
    || d?.default_card_back?.image_url
    || d?.default_card_back?.preview_url
    || ''
}
function currentDeckSlug(v) { return v?.deck_template_slug || '' }
function heroUrl(v) {
  if (v?.skill_key === 'tarot') {
    return v?.card_back_image_url
      || v?.deck_template_back_url
      || v?.deck_template_cover
      || ''
  }
  return v?.symbology_pool_cover || ''
}
function heroAlt(v) {
  return v?.deck_template_name || v?.symbology_pool_name || 'current deck'
}
function heroTitle(v) {
  return v?.deck_template_name || v?.symbology_pool_name || 'Untitled'
}
function formatSkill(key) {
  return (key || '').charAt(0).toUpperCase() + (key || '').slice(1)
}

// ── Tile click → unified picker modal ─────────────────────────
function openDeckPicker(deck, variant) {
  pickerDeck.value = deck
  pickerVariant.value = variant
}
function closePicker() {
  pickerDeck.value = null
  pickerVariant.value = null
  // Refresh My Decks so the Favorites + Custom Decks galleries
  // pick up any change made from the picker (Add to Favorites,
  // Remove from Favorites, Set as Default).
  loadMyDecks()
}

function onSettingsSaved(refreshedVariant) {
  if (refreshedVariant) {
    const list = (store.readerSkillVariants || []).slice()
    const idx = list.findIndex(x => x.id === refreshedVariant.id)
    if (idx >= 0) list[idx] = refreshedVariant
    else list.push(refreshedVariant)
    store.readerSkillVariants = list
  }
}

</script>

<style scoped>
.skills-editor {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ── Skill tabs ─────────────────────────────────────────────── */
.skill-tabs {
  display: flex;
  gap: 2px;
  border-bottom: 1px solid rgba(195, 169, 255, 0.18);
  flex-wrap: wrap;
}
.skill-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: rgba(212, 196, 232, 0.65);
  padding: 10px 18px;
  cursor: pointer;
  font-family: 'Cinzel', serif;
  font-size: 13px;
  letter-spacing: 0.04em;
  transition: all 0.18s;
}
.skill-tab:hover { color: #f3e9d2; }
.skill-tab.active {
  color: #C9A94E;
  border-bottom-color: #C9A94E;
}
.skill-tab .tab-icon { font-size: 16px; }
.skill-tab.add-skill {
  color: rgba(195, 169, 255, 0.6);
  margin-left: auto;
  font-size: 12px;
  border: 1px dashed rgba(195, 169, 255, 0.22);
  border-radius: 8px 8px 0 0;
}
.skill-tab.add-skill:hover { color: #C9A94E; border-color: #C9A94E; }

.add-skill-stub {
  text-align: center;
  padding: 56px 24px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(195, 169, 255, 0.18);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.add-skill-stub .stub-icon {
  font-size: 36px;
  color: rgba(201, 169, 78, 0.55);
}
.add-skill-stub h4 {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  margin: 0;
  font-size: 16px;
}
.add-skill-stub p {
  color: rgba(212, 196, 232, 0.7);
  font-size: 13px;
  max-width: 460px;
  line-height: 1.55;
  margin: 0;
}

.variant-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Slim context bar ─────────────────────────────────────────── */
.ctx-bar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.builder-wrap {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.back-link {
  align-self: flex-start;
  background: transparent;
  border: none;
  color: rgba(212, 196, 232, 0.7);
  font-size: 12px;
  cursor: pointer;
  font-family: inherit;
  padding: 4px 0;
}
.back-link:hover { color: #C9A94E; }

.ghost-btn, .primary-btn {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.18s;
}
.ghost-btn {
  background: transparent;
  border: 1px solid rgba(195,169,255,0.28);
  color: rgba(212, 196, 232, 0.85);
}
.ghost-btn:hover {
  border-color: #C9A94E;
  color: #C9A94E;
}
.primary-btn {
  background: #663399;
  color: #fff;
  border: none;
  padding: 10px 22px;
  font-size: 13px;
}
.primary-btn:hover { background: #7a3fb1; }

/* ── Mode tabs ───────────────────────────────────────────────── */
.mode-tabs {
  display: inline-flex;
  align-self: flex-start;
  gap: 4px;
  padding: 4px;
  background: rgba(0,0,0,0.32);
  border: 1px solid rgba(195,169,255,0.16);
  border-radius: 10px;
}
.mode-tab {
  padding: 8px 16px;
  border-radius: 7px;
  border: none;
  background: transparent;
  color: rgba(212, 196, 232, 0.7);
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  letter-spacing: 0.04em;
  transition: all 0.18s;
}
.mode-tab:hover { color: rgba(212, 196, 232, 0.95); }
.mode-tab.active {
  background: rgba(102,51,153,0.32);
  color: #fff;
  box-shadow: inset 0 0 0 1px rgba(195,169,255,0.32);
}

/* ── Gallery ──────────────────────────────────────────────────── */
.gallery-wrap { padding-top: 4px; }
.status {
  text-align: center;
  padding: 36px 12px;
  color: rgba(212, 196, 232, 0.55);
  font-style: italic;
}
/* ── The gallery: a row of card backs, the deck in use first ───── */
/* Tiles are sized so five or six read across the pane — a card back is
   legible at 130px, and a gallery is for comparing, not admiring one. */
.gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 18px 14px;
}
.tile {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: inherit;
  color: inherit;
  text-align: center;
  position: relative;
  transition: transform 0.18s;
}
.tile:hover { transform: translateY(-3px); }
.tile:focus-visible { outline: 2px solid #E0BC60; outline-offset: 4px; border-radius: 12px; }
.tile-back {
  position: relative;
  aspect-ratio: 2 / 3;
  background: rgba(0,0,0,0.45);
  border: 1px solid rgba(195,169,255,0.18);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(0,0,0,0.35);
  transition: border-color 0.18s, box-shadow 0.18s;
}
.tile:hover .tile-back { border-color: rgba(195,169,255,0.5); }
.tile-back img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.tile-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: rgba(212,196,232,0.5);
  font-size: 11px;
  font-style: italic;
  text-align: center;
  padding: 12px;
}
.placeholder-icon {
  font-size: 28px;
  color: rgba(195,169,255,0.35);
}
.tile-name {
  font-family: 'Cinzel', serif;
  color: rgba(232, 216, 255, 0.88);
  font-size: 12px;
  letter-spacing: 0.02em;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 2px;
}

/* The deck in use: a gold ring on the back and a ribbon inside the frame.
   Nothing sits above or below the tile, so the row stays a row. */
.active-ribbon {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  font-family: 'Cinzel', serif;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #1a0c2e;
  background: linear-gradient(135deg, #E0BC60 0%, #C9A94E 100%);
  border-radius: 999px;
  padding: 3px 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.35);
}
.tile.current .tile-back {
  border-color: #C9A94E;
  box-shadow:
    0 0 0 2px rgba(201,169,78,0.55),
    0 8px 24px rgba(0,0,0,0.45),
    0 0 26px rgba(201,169,78,0.28);
}
.tile.current:hover .tile-back { border-color: #E0BC60; }
.tile.current .tile-name { color: #E0BC60; }

/* ── Custom Deck stub ─────────────────────────────────────────── */
.custom-stub {
  text-align: center;
  padding: 48px 24px;
  background: rgba(255,255,255,0.02);
  border: 1px dashed rgba(195,169,255,0.22);
  border-radius: 12px;
  color: rgba(212, 196, 232, 0.75);
}
.custom-stub h4 {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 16px;
  margin: 0 0 8px;
}
.custom-stub p {
  margin: 0 auto;
  max-width: 380px;
  font-size: 13px;
  line-height: 1.5;
}
.stub-icon {
  font-size: 36px;
  color: rgba(201,169,78,0.45);
  margin-bottom: 8px;
}

/* ── Astrology fallback (kept until separate refactor) ───────── */
.astrology-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}
.hero-frame {
  width: 280px;
  aspect-ratio: 2 / 3;
  background: rgba(0,0,0,0.35);
  border-radius: 14px;
  border: 1px solid rgba(195,169,255,0.18);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 34px rgba(0,0,0,0.45), 0 0 26px rgba(201,169,78,0.12);
}
.hero-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.hero-placeholder {
  color: rgba(212, 196, 232, 0.45);
  font-size: 13px;
  font-style: italic;
}
.hero-title {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 18px;
  margin: 0;
  text-transform: capitalize;
}

.error {
  color: rgba(244, 67, 54, 0.9);
  font-size: 12px;
  margin: 8px 0 0;
}

/* ── Custom-deck detail modal — fills most of the viewport so the
     78-card grid + card back can breathe. ──────────────────────── */
.deck-detail-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
}
.deck-detail-card {
  position: relative;
  background: #1a0c2e;
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 14px;
  width: 100%;
  max-width: 1280px;
  height: 95vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.deck-detail-close {
  position: absolute;
  top: 12px;
  right: 14px;
  z-index: 2;
  background: rgba(0, 0, 0, 0.55);
  border: 1px solid rgba(195, 169, 255, 0.22);
  color: rgba(212, 196, 232, 0.85);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  font-family: inherit;
}
.deck-detail-close:hover {
  color: #fff;
  border-color: #C9A94E;
  background: rgba(0, 0, 0, 0.75);
}
.deck-detail-body {
  flex: 1;
  overflow-y: auto;
  /* Top padding leaves room for the absolute-positioned close button
     so the inner CustomDeckBuilder's "Set as Default" header CTA
     doesn't collide with the × at the modal's top-right. */
  padding: 56px 28px 28px;
}

@media (max-width: 720px) {
  .gallery {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
  .deck-detail-backdrop { padding: 0; }
  .deck-detail-card {
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
    border: none;
  }
  .deck-detail-body { padding: 56px 16px 24px; }
}
</style>
